import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  CircularProgress,
  Fade,
  Paper,
  Typography,
  Drawer,
  Divider,
  Badge,
  Tooltip,
  IconButton,
} from '@mui/material';
import Header from './Header.tsx';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { AiActions } from '../../../Services/_actions/index.js';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import QuestionCard from './QuestionCard.tsx';
import EditQuestionModal from './EditQuestionModal.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import BulkUploadModal from './Bulkuploadmodal.tsx';

interface QuestionBankViewProps {
  selectedBoard: string;
  selectedClass: string;
  selectedSubject: string;
  onBack: () => void;
  onClearFilters: () => void;
  knowledgeMapData?: any[];
}

const EMPTY_QUESTION = {
  question_id: '',
  question_text: '',
  question: '',
  options: [
    { text: '', is_correct: true, correct_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
  ],
  topic: '',
  subtopic: '',
  difficulty: 'medium',
  question_type: 'MCQ',
  reasoning: '',
  incorrect_reasoning: '',
  verified: false,
};

const QuestionBankView: React.FC<QuestionBankViewProps> = ({
  selectedBoard,
  selectedClass,
  selectedSubject,
  onBack,
  onClearFilters,
  knowledgeMapData = [],
}) => {
  const actions = AiActions();
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [localVerified, setLocalVerified] = React.useState<string[]>([]);
  const [deletingQuestions, setDeletingQuestions] = React.useState(false);
  const [questions, setQuestions] = React.useState<any[]>([]);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [difficultyFilter, setDifficultyFilter] = React.useState<string | null>(null);
  const [totalCount, setTotalCount] = React.useState(0);
  const [difficultyCount, setDifficultyCount] = React.useState({ easy: 0, medium: 0, hard: 0 });
  const LIMIT = 100;
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [loadingQuestions, setLoadingQuestions] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [subjectFilter, setSubjectFilter] = React.useState<string>('All');
  const [classFilter, setClassFilter] = React.useState<string>('All');
  const [topicFilter, setTopicFilter] = React.useState<string>('All');
  const [subtopicFilter, setSubtopicFilter] = React.useState<string>('All');

  // Count active filters for badge
  const activeFilterCount = [
    classFilter !== 'All',
    subjectFilter !== 'All',
    topicFilter !== 'All',
    subtopicFilter !== 'All',
    !!difficultyFilter,
  ].filter(Boolean).length;

  const handleClassChange = (selected: any) => {
    setClassFilter(selected?.value || 'All');
    setSubjectFilter('All');
    setTopicFilter('All');
    setSubtopicFilter('All');
  };

  const classOptions = React.useMemo(() => {
    const classes = knowledgeMapData?.map((item: any) => item.class) || [];
    return ['All', ...new Set(classes)].map(c => ({ label: c === 'All' ? 'All Classes' : `Class ${c}`, value: c }));
  }, [knowledgeMapData]);

  const topicOptions = React.useMemo(() => {
    const base = [{ label: 'All Topics', value: 'All' }];
    if (subjectFilter === 'All') return base;
    const topics =
      knowledgeMapData
        ?.filter((item: any) =>
          String(item.subject).toLowerCase() === subjectFilter.toLowerCase() &&
          (classFilter === 'All' || String(item.class) === String(classFilter))
        )
        ?.flatMap((item: any) => (item.topics || []).map((t: any) => t.topic)) || [];
    return [...base, ...[...new Set(topics)].map((t) => ({ label: t, value: t }))];
  }, [knowledgeMapData, subjectFilter, classFilter]);

  const subtopicOptions = React.useMemo(() => {
    const base = [{ label: 'All Subtopics', value: 'All' }];
    if (topicFilter === 'All') return base;
    const subtopics =
      knowledgeMapData
        ?.filter((item: any) =>
          String(item.subject).toLowerCase() === subjectFilter.toLowerCase() &&
          (classFilter === 'All' || String(item.class) === String(classFilter))
        )
        ?.flatMap((item: any) =>
          (item.topics || [])
            .filter((t: any) => t.topic === topicFilter)
            .flatMap((t: any) => (t.subtopics || []).map((s: any) => s.subtopic))
        ) || [];
    return [...base, ...[...new Set(subtopics)].map((s) => ({ label: s, value: s }))];
  }, [knowledgeMapData, topicFilter, subjectFilter, classFilter]);

  const subjectOptions = React.useMemo(() => {
    const base = [{ label: 'All Subjects', value: 'All' }];
    if (classFilter === 'All') {
      const subjects = knowledgeMapData?.map((item: any) => item.subject) || [];
      return [...base, ...[...new Set(subjects)].map(s => ({ label: s, value: s }))];
    }
    const subjects = knowledgeMapData
      ?.filter((item: any) => String(item.class) === String(classFilter))
      ?.map((item: any) => item.subject) || [];
    return [...base, ...[...new Set(subjects)].map(s => ({ label: s, value: s }))];
  }, [knowledgeMapData, classFilter]);

  const filteredDifficultyCount = React.useMemo(() => {
    const base = questions.filter((q: any) => {
      const subjectMatch = subjectFilter === 'All' ? true : String(q.subject).toLowerCase() === subjectFilter.toLowerCase();
      const classMatch = classFilter === 'All' ? true : String(q.class).toLowerCase() === classFilter.toLowerCase();
      const topicMatch = topicFilter === 'All' ? true : String(q.topic).toLowerCase() === topicFilter.toLowerCase();
      const subtopicMatch = subtopicFilter === 'All' ? true : String(q.subtopic).toLowerCase() === subtopicFilter.toLowerCase();
      return subjectMatch && classMatch && topicMatch && subtopicMatch;
    });
    return {
      easy: base.filter(q => String(q.difficulty).replace(/[\[\]'"]/g, '').trim().toLowerCase() === 'easy').length,
      medium: base.filter(q => String(q.difficulty).replace(/[\[\]'"]/g, '').trim().toLowerCase() === 'medium').length,
      hard: base.filter(q => String(q.difficulty).replace(/[\[\]'"]/g, '').trim().toLowerCase() === 'hard').length,
    };
  }, [questions, subjectFilter, classFilter, topicFilter, subtopicFilter]);


  const difficultyOptions = React.useMemo(() => [
    { label: `Easy (${filteredDifficultyCount.easy})`, value: 'easy' },
    { label: `Medium (${filteredDifficultyCount.medium})`, value: 'medium' },
    { label: `Hard (${filteredDifficultyCount.hard})`, value: 'hard' },
  ], [filteredDifficultyCount]);

  React.useEffect(() => { fetchInitialQuestions(); }, []);

  const fetchInitialQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const response = await actions.getQuestions({
        board: selectedBoard,
        class: selectedClass,
        subject: selectedSubject,
        limit: LIMIT,
        offset: 0,
      });
      const data = response?.data?.list || [];
      const total = response?.data?.pagination?.total;
      const diffCount = response?.data?.difficulty_count || {};
      setDifficultyCount({ easy: diffCount.easy || 0, medium: diffCount.medium || 0, hard: diffCount.hard || 0 });
      setQuestions(data);
      setTotalCount(total);
      setOffset(LIMIT);
      setHasMore(data.length === LIMIT);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const loadMoreQuestions = async () => {
    if (!hasMore || loadingMore) return;
    try {
      setLoadingMore(true);
      const response = await actions.getQuestions({
        board: selectedBoard,
        class: selectedClass,
        subject: selectedSubject,
        difficulty: difficultyFilter || undefined,
        limit: LIMIT,
        offset: offset,
      });
      const newData = response?.data?.list || [];
      setQuestions(prev => [...prev, ...newData]);
      setOffset(prev => prev + LIMIT);
      if (newData.length < LIMIT) setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el || loadingMore || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) loadMoreQuestions();
    };
    const el = scrollRef.current;
    el?.addEventListener('scroll', handleScroll);
    return () => el?.removeEventListener('scroll', handleScroll);
  }, [offset, hasMore, loadingMore]);

  const getQuestionId = (question: any): string => String(question?.question_id || question?.id || '');

  const handleSelect = (id: string) => {
    if (!id) return;
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const handleQuestionCreated = (newQuestion: any) => {
    setQuestions(prev => [newQuestion, ...prev]);
    setTotalCount(prev => prev + 1);
    setCreateModalOpen(false);
    fetchInitialQuestions();
  };

  const handleVerify = async () => {
    if (selectedIds.length === 0) return;
    setLocalVerified(prev => [...prev, ...selectedIds]);
    await actions.verifyMe({ question_ids: selectedIds });
    setSelectedIds([]);
  };

  const handleDeleteClick = () => {
    if (selectedIds.length === 0 || deletingQuestions) return;
    setConfirmOpen(true);
  };

  const handleConfirmClose = async (confirmed: boolean) => {
    setConfirmOpen(false);
    if (!confirmed) return;
    try {
      setDeletingQuestions(true);
      await actions.deleteQuestions(selectedIds);
      setQuestions(prev => prev.filter(q => !selectedIds.includes(getQuestionId(q))));
      setLocalVerified(prev => prev.filter(id => !selectedIds.includes(id)));
      setSelectedIds([]);
    } finally {
      setDeletingQuestions(false);
    }
  };

  const filteredQuestions = React.useMemo(() => {
    return questions.filter((q: any) => {
      const difficultyMatch = difficultyFilter
        ? String(q.difficulty).replace(/[\[\]'"]/g, '').trim().toLowerCase() === difficultyFilter
        : true;
      const subjectMatch = subjectFilter === 'All' ? true : String(q.subject).toLowerCase() === subjectFilter.toLowerCase();
      const classMatch = classFilter === 'All' ? true : String(q.class).toLowerCase() === classFilter.toLowerCase();
      const topicMatch = topicFilter === 'All' ? true : String(q.topic).toLowerCase() === topicFilter.toLowerCase();
      const subtopicMatch = subtopicFilter === 'All' ? true : String(q.subtopic).toLowerCase() === subtopicFilter.toLowerCase();
      return difficultyMatch && subjectMatch && classMatch && topicMatch && subtopicMatch;
    });
  }, [questions, difficultyFilter, subjectFilter, classFilter, topicFilter, subtopicFilter]);

  const handleDifficultyClick = async (difficulty: string) => {
    try {
      setDifficultyFilter(difficulty);
      setLoadingQuestions(true);
      setOffset(0);
      setHasMore(true);
      const response = await actions.getQuestions({
        board: selectedBoard,
        class: selectedClass,
        subject: selectedSubject,
        difficulty: difficulty,
        limit: 100,
        offset: 0,
      });
      const data = response?.data?.list || [];
      setQuestions(data);
      setOffset(LIMIT);
      setHasMore(data.length === LIMIT);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const clearDifficultyFilter = async () => {
    setDifficultyFilter(null);
    fetchInitialQuestions();
  };

  const clearAllFilters = () => {
    setClassFilter('All');
    setSubjectFilter('All');
    setTopicFilter('All');
    setSubtopicFilter('All');
    clearDifficultyFilter();
  };

  const difficultyConfig = [
    { key: 'easy', label: 'Easy', color: '#2e7d32', bg: '#e8f5e9' },
    { key: 'medium', label: 'Medium', color: '#e65100', bg: '#fff3e0' },
    { key: 'hard', label: 'Hard', color: '#c62828', bg: '#ffebee' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <Header
        title="Question Bank"
        actions={
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchInitialQuestions}
              disabled={loadingQuestions}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: 11,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {loadingQuestions ? 'Loading...' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onBack}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: 11,
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Back to Generator
            </Button>
          </>
        }
      />

      <Container maxWidth="100% !important" sx={{ pt: 3, pb: 4 }}>
        <Fade in={true}>
          <Box>
            {/* Active Filters Display */}
            {(selectedBoard || selectedClass || selectedSubject) && (
              <Paper
                elevation={0}
                sx={{
                  p: '10px 16px', mb: 2, borderRadius: 2,
                  border: '1px solid #e0e0e0', bgcolor: '#fff',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontSize: 11, color: '#555', mr: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Filters:
                  </Typography>
                  {selectedBoard && <Chip label={selectedBoard} size="small" sx={{ fontSize: 10, height: 22, bgcolor: '#e8eaf6', color: '#3949ab', }} />}
                  {selectedClass && <Chip label={`Class ${selectedClass}`} size="small" sx={{ fontSize: 10, height: 22, bgcolor: '#e8eaf6', color: '#3949ab', }} />}
                  {selectedSubject && <Chip label={selectedSubject} size="small" sx={{ fontSize: 10, height: 22, bgcolor: '#e8eaf6', color: '#3949ab', }} />}
                </Box>
                <Button size="small" onClick={onClearFilters} sx={{ textTransform: 'none', fontSize: 10, color: '#e53935', minWidth: 0, px: 1 }}>
                  Clear
                </Button>
              </Paper>
            )}

            {/* Questions Section */}
            {loadingQuestions ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress size={36} thickness={4} />
                <Typography variant="body2" sx={{ mt: 2, color: '#888', fontSize: 12 }}>
                  Loading questions...
                </Typography>
              </Box>
            ) : questions?.length > 0 ? (
              <Box ref={scrollRef} sx={{ height: '82vh', overflowY: 'auto', pr: 0.5 }}>

                {/* Toolbar */}
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                    p: '10px 14px', mb: 2, borderRadius: 2,
                    border: '1px solid #e0e0e0', bgcolor: '#fff',
                    position: 'sticky', top: 0, zIndex: 10,
                  }}
                >
                  {/* Question Count */}
                  <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: 13, color: '#1a237e' }}>
                      {filteredQuestions.length}
                      <Box component="span" sx={{ fontWeight: 400, color: '#888', fontSize: 12 }}>
                        {' '}/ {totalCount} questions
                      </Box>
                    </Typography>

                    {/* Active filter chips inline summary */}
                    {difficultyFilter && (
                      <Chip
                        label={difficultyFilter}
                        size="small"
                        onDelete={clearDifficultyFilter}
                        sx={{
                          fontSize: 10, height: 20,
                          bgcolor: difficultyConfig.find(d => d.key === difficultyFilter)?.bg,
                          color: difficultyConfig.find(d => d.key === difficultyFilter)?.color,
                        }}
                      />
                    )}
                    {subjectFilter !== 'All' && (
                      <Chip label={subjectFilter} size="small" onDelete={() => { setSubjectFilter('All'); setTopicFilter('All'); setSubtopicFilter('All'); }}
                        sx={{ fontSize: 10, height: 20, bgcolor: '#e3f2fd', color: '#1565c0', }} />
                    )}
                    {topicFilter !== 'All' && (
                      <Chip label={topicFilter} size="small" onDelete={() => { setTopicFilter('All'); setSubtopicFilter('All'); }}
                        sx={{ fontSize: 10, height: 20, bgcolor: '#f3e5f5', color: '#6a1b9a', }} />
                    )}
                    {subtopicFilter !== 'All' && (
                      <Chip label={subtopicFilter} size="small" onDelete={() => setSubtopicFilter('All')}
                        sx={{ fontSize: 10, height: 20, bgcolor: '#fce4ec', color: '#880e4f', }} />
                    )}
                    {classFilter !== 'All' && (
                      <Chip label={`Class ${classFilter}`} size="small" onDelete={() => { setClassFilter('All'); setSubjectFilter('All'); setTopicFilter('All'); setSubtopicFilter('All'); }}
                        sx={{ fontSize: 10, height: 20, bgcolor: '#e8f5e9', color: '#1b5e20', }} />
                    )}
                  </Box>

                  {/* Action Buttons */}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setCreateModalOpen(true)}
                    sx={{
                      textTransform: 'none', fontSize: 11,
                      bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' },
                      px: 1.5, height: 32, borderRadius: 1.5,
                    }}
                  >
                    + Create Question
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    disabled={selectedIds.length === 0}
                    onClick={handleVerify}
                    sx={{
                      textTransform: 'none', fontSize: 11,
                      bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' },
                      '&:disabled': { bgcolor: '#e0e0e0', color: '#bdbdbd' },
                      px: 1.5, height: 32, borderRadius: 1.5,
                    }}
                  >
                    Verify ({selectedIds.length})
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    disabled={selectedIds.length === 0 || deletingQuestions}
                    onClick={handleDeleteClick}
                    sx={{
                      textTransform: 'none', fontSize: 11,
                      px: 1.5, height: 32, borderRadius: 1.5,
                      '&:disabled': { bgcolor: '#e0e0e0', color: '#bdbdbd' },
                    }}
                  >
                    {deletingQuestions ? 'Deleting...' : `Delete (${selectedIds.length})`}
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setBulkUploadOpen(true)}
                    sx={{
                      textTransform: 'none', fontSize: 11,
                      borderColor: '#9e9e9e', color: '#424242',
                      '&:hover': { borderColor: '#616161', bgcolor: '#f5f5f5' },
                      px: 1.5, height: 32, borderRadius: 1.5,
                    }}
                  >
                    Bulk Upload
                  </Button>

                  {/* ── Filter Icon Button ── */}
                  <Tooltip title="Filters" arrow placement="top">
                    <Badge
                      badgeContent={activeFilterCount}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: 9, height: 16, minWidth: 16, padding: '0 4px',
                        }
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setFilterDrawerOpen(true)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: activeFilterCount > 0 ? '#1565c0' : '#e0e0e0',
                          bgcolor: activeFilterCount > 0 ? '#e3f2fd' : '#fafafa',
                          color: activeFilterCount > 0 ? '#1565c0' : '#616161',
                          '&:hover': {
                            bgcolor: activeFilterCount > 0 ? '#bbdefb' : '#f0f0f0',
                            borderColor: '#1565c0',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <TuneIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                </Paper>

                {/* Question Cards */}
                {filteredQuestions.map((question: any, index: number) => {
                  const qId = getQuestionId(question);
                  const mergedQuestion = localVerified.includes(qId) ? { ...question, verified: true } : question;
                  return (
                    <QuestionCard
                      key={qId || index}
                      knowledgeMapData={knowledgeMapData}
                      question={mergedQuestion}
                      index={index}
                      selectable={true}
                      selected={selectedIds.includes(qId)}
                      onSelect={() => handleSelect(qId)}
                      onQuestionUpdate={(updated) => {
                        setQuestions(prev => prev.map(q => getQuestionId(q) === getQuestionId(updated) ? updated : q));
                      }}
                    />
                  );
                })}

                {loadingMore && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={28} thickness={4} />
                  </Box>
                )}

                {!hasMore && (
                  <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: '#bbb', fontSize: 11 }}>
                    — End of results —
                  </Typography>
                )}
              </Box>

            ) : (
              <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                <Typography variant="h6" sx={{ mb: 1, fontSize: 15, color: '#555', }}>
                  No questions found
                </Typography>
                <Typography variant="body2" sx={{ color: '#999', fontSize: 12, mb: 2 }}>
                  {selectedBoard || selectedClass || selectedSubject
                    ? 'Try adjusting your filters or generate new questions.'
                    : 'Start by generating questions or adjust your filters.'}
                </Typography>
                <Button variant="outlined" onClick={onBack} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Go to Generator
                </Button>
              </Paper>
            )}
          </Box>
        </Fade>
      </Container>

      {/* ─────────────────────── Filter Drawer ─────────────────────── */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: '16px 0 0 16px',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.10)',
          }
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2.5, py: 1.8,
            borderBottom: '1px solid #f0f0f0',
            bgcolor: '#fafafa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ fontSize: 18, color: '#1565c0' }} />
            <Typography sx={{ fontSize: 13, color: '#1a237e' }}>
              Filters
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                label={`${activeFilterCount} active`}
                size="small"
                sx={{ fontSize: 9, height: 18, bgcolor: '#e3f2fd', color: '#1565c0', }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {activeFilterCount > 0 && (
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{ fontSize: 10, textTransform: 'none', color: '#e53935', minWidth: 0, px: 1, py: 0.5 }}
              >
                Clear all
              </Button>
            )}
            <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: '#888' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>

          {/* Difficulty Section */}
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2 }}>
              Difficulty
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {difficultyConfig.map(({ key, label, color, bg }) => (
                <Box
                  key={key}
                  onClick={() => difficultyFilter === key ? clearDifficultyFilter() : handleDifficultyClick(key)}
                  sx={{
                    flex: 1, textAlign: 'center', cursor: 'pointer',
                    py: 1, px: 0.5, borderRadius: 2,
                    border: '1.5px solid',
                    borderColor: difficultyFilter === key ? color : '#e8e8e8',
                    bgcolor: difficultyFilter === key ? bg : '#fff',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: color, bgcolor: bg },
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: difficultyFilter === key ? color : '#555' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: difficultyFilter === key ? color : '#999' }}>
                    {filteredDifficultyCount[key as keyof typeof filteredDifficultyCount]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#f0f0f0' }} />

          {/* Class Filter */}
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2 }}>
              Class
            </Typography>
            <SearchableDropdown
              className="new-serachable-dropdown"
              options={classOptions}
              labelKey="label"
              valueKey="value"
              label="Select Class"
              size="small"
              value={classOptions.find(o => o.value === classFilter) || null}
              onChange={handleClassChange}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Subject Filter */}
          <Box>
            <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2 }}>
              Subject
            </Typography>
            <SearchableDropdown
              className="new-serachable-dropdown"
              options={subjectOptions}
              labelKey="label"
              valueKey="value"
              label="Select Subject"
              size="small"
              value={subjectOptions.find(o => o.value === subjectFilter) || null}
              onChange={(selected: any) => {
                setSubjectFilter(selected?.value || 'All');
                setTopicFilter('All');
                setSubtopicFilter('All');
              }}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Topic Filter — shown only when subject is selected */}
          {subjectFilter !== 'All' && (
            <Box>
              <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2 }}>
                Topic
              </Typography>
              <SearchableDropdown
                className="new-serachable-dropdown"
                options={topicOptions}
                labelKey="label"
                valueKey="value"
                label="Select Topic"
                size="small"
                value={topicOptions.find(o => o.value === topicFilter) || null}
                onChange={(selected: any) => {
                  setTopicFilter(selected?.value || 'All');
                  setSubtopicFilter('All');
                }}
                sx={{ width: '100%' }}
              />
            </Box>
          )}

          {/* Subtopic Filter — shown only when topic is selected */}
          {topicFilter !== 'All' && (
            <Box>
              <Typography sx={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.2 }}>
                Subtopic
              </Typography>
              <SearchableDropdown
                className="new-serachable-dropdown"
                options={subtopicOptions}
                labelKey="label"
                valueKey="value"
                label="Select Subtopic"
                size="small"
                value={subtopicOptions.find(o => o.value === subtopicFilter) || null}
                onChange={(selected: any) => setSubtopicFilter(selected?.value || 'All')}
                sx={{ width: '100%' }}
              />
            </Box>
          )}
        </Box>

        {/* Drawer Footer */}
        <Box
          sx={{
            mt: 'auto', px: 2.5, py: 2,
            borderTop: '1px solid #f0f0f0',
            bgcolor: '#fafafa',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="small"
            onClick={() => setFilterDrawerOpen(false)}
            sx={{
              textTransform: 'none', fontSize: 12,
              bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' },
              height: 36, borderRadius: 2,
            }}
          >
            Apply Filters
            {activeFilterCount > 0 && ` (${filteredQuestions.length} results)`}
          </Button>
        </Box>
      </Drawer>

      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmClose}
        options={{
          title: 'Delete Questions?',
          text: `Are you sure you want to delete ${selectedIds.length} selected question(s)? This action cannot be undone.`,
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'secondary',
        }}
      />

      {createModalOpen && (
        <EditQuestionModal
          open={createModalOpen}
          question={{ ...EMPTY_QUESTION, board: selectedBoard, class: selectedClass, subject: selectedSubject } as any}
          mode="create"
          onClose={() => setCreateModalOpen(false)}
          onSave={handleQuestionCreated}
          knowledgeMapData={knowledgeMapData}
          selectedBoard={selectedBoard}
          selectedClass={selectedClass}
          selectedSubject={selectedSubject}
        />
      )}

      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        selectedBoard={selectedBoard}
        selectedClass={selectedClass}
        selectedSubject={selectedSubject}
        onUploadSuccess={fetchInitialQuestions}
      />
    </Box>
  );
};

export default QuestionBankView;